 

import { useState } from "react"
import "../styles/info-panel.css"

export default function InfoPanel() {
  const [activeTab, setActiveTab] = useState("problem")

  return (
    <div className="tsp-info-panel">
      <div className="tsp-info-tabs">
        <button className={`tsp-info-tab ${activeTab === "problem" ? "active" : ""}`} onClick={() => setActiveTab("problem")}>
          The Problem
        </button>
        <button
          className={`tsp-info-tab ${activeTab === "algorithms" ? "active" : ""}`}
          onClick={() => setActiveTab("algorithms")}
        >
          Algorithms
        </button>
        <button
          className={`tsp-info-tab ${activeTab === "complexity" ? "active" : ""}`}
          onClick={() => setActiveTab("complexity")}
        >
          Complexity
        </button>
        <button
          className={`tsp-info-tab ${activeTab === "instructions" ? "active" : ""}`}
          onClick={() => setActiveTab("instructions")}
        >
          How to Use
        </button>
      </div>

      <div className="tsp-info-tab-content">
        {activeTab === "problem" && (
          <div>
            <h3>What is the Traveling Salesman Problem?</h3>
            <p>
              The Traveling Salesman Problem (TSP) is one of the most famous problems in computer science. It asks:
              "Given a list of cities and the distances between each pair of cities, what is the shortest possible route
              that visits each city exactly once and returns to the origin city?"
            </p>
            <p>
              Despite its simple description, the TSP is notoriously difficult to solve optimally for large numbers of
              cities. It belongs to the class of NP-hard problems, which means there is no known algorithm that can
              solve it efficiently for all cases.
            </p>
            <div className="info-highlight">
              <strong>Real-world applications:</strong> Route planning, logistics, circuit board drilling, DNA
              sequencing, and many more.
            </div>
          </div>
        )}

        {activeTab === "algorithms" && (
          <div>
            <h3>TSP Algorithms</h3>
            <p>This visualizer implements several algorithms for solving the TSP:</p>
            <div className="algorithm-card">
              <h4>Nearest Neighbor</h4>
              <p>A greedy algorithm that always visits the nearest unvisited city.</p>
              <ul>
                <li>
                  <strong>Pros:</strong> Fast, simple to implement
                </li>
                <li>
                  <strong>Cons:</strong> Often produces suboptimal solutions
                </li>
              </ul>
            </div>

            <div className="algorithm-card">
              <h4>2-Opt</h4>
              <p>
                An improvement algorithm that iteratively removes two edges and reconnects the tour in a different way
                to reduce the total distance.
              </p>
              <ul>
                <li>
                  <strong>Pros:</strong> Good balance of speed and quality
                </li>
                <li>
                  <strong>Cons:</strong> Can get stuck in local optima
                </li>
              </ul>
            </div>

            <div className="algorithm-card">
              <h4>Brute Force</h4>
              <p>Examines all possible permutations of cities to find the optimal solution.</p>
              <ul>
                <li>
                  <strong>Pros:</strong> Guaranteed to find the optimal solution
                </li>
                <li>
                  <strong>Cons:</strong> Extremely slow (O(n!) complexity)
                </li>
              </ul>
            </div>

            <div className="algorithm-card">
              <h4>Genetic Algorithm</h4>
              <p>
                Inspired by natural selection, it evolves a population of solutions over multiple generations to find
                increasingly better tours.
              </p>
              <ul>
                <li>
                  <strong>Pros:</strong> Can handle large instances, finds good solutions
                </li>
                <li>
                  <strong>Cons:</strong> Results may vary, requires parameter tuning
                </li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === "complexity" && (
          <div>
            <h3>Computational Complexity</h3>
            <p>The TSP is an NP-hard problem, which means:</p>
            <ul>
              <li>No known algorithm can solve it optimally in polynomial time</li>
              <li>
                As the number of cities increases, the time required to find the optimal solution grows factorially
              </li>
              <li>For n cities, there are (n-1)!/2 possible routes to consider in the worst case</li>
            </ul>

            <div className="complexity-table-container">
              <table className="complexity-table">
                <thead>
                  <tr>
                    <th>Algorithm</th>
                    <th>Time Complexity</th>
                    <th>Solution Quality</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Nearest Neighbor</td>
                    <td>O(n²)</td>
                    <td>Can be up to 25% worse than optimal</td>
                  </tr>
                  <tr>
                    <td>2-Opt</td>
                    <td>O(n²) per iteration</td>
                    <td>Often within 5% of optimal</td>
                  </tr>
                  <tr>
                    <td>Brute Force</td>
                    <td>O(n!)</td>
                    <td>Optimal (but impractical for n &gt; 12)</td>
                  </tr>
                  <tr>
                    <td>Genetic Algorithm</td>
                    <td>O(g·p·n²) where g=generations, p=population</td>
                    <td>Varies, often good with proper tuning</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="info-highlight">
              <strong>Note:</strong> For just 20 cities, there are over 10^18 possible routes - more than the number of
              seconds since the Big Bang!
            </div>
          </div>
        )}

        {activeTab === "instructions" && (
          <div>
            <h3>How to Use This Visualizer</h3>

            <div className="instruction-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <strong>Add Cities:</strong> Click anywhere on the graph area to add cities, or use the "Random Cities"
                control to generate a random set.
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <strong>Select Algorithm:</strong> Choose one of the available algorithms from the dropdown menu.
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <strong>Run Algorithm:</strong> Click the "Run Algorithm" button to start the visualization.
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <strong>Control Steps:</strong> Use the step controls to navigate through the algorithm's execution:
                <ul>
                  <li>Step forward/backward to see each stage</li>
                  <li>Play/pause for automatic progression</li>
                  <li>Jump to first/last step</li>
                </ul>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">5</div>
              <div className="step-content">
                <strong>Examine Logs:</strong> The comparison logs at the bottom show detailed information about what's
                happening at each step.
              </div>
            </div>

            <div className="info-highlight">
              <strong>Tip:</strong> Try different algorithms on the same set of cities to compare their performance and
              solution quality!
            </div>
          </div>
        )}
      </div>
    </div>
  )
}